import { useMemo, useState } from "react";
import { MapPinIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, EmptyState, ErrorText, Row } from "@/components";
import { AutosaveFormDateInput } from "@/components/companies/shared/autosave-form/AutosaveFormDateInput";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormNumberInput } from "@/components/companies/shared/autosave-form/AutosaveFormNumberInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { IntakeTabBar } from "@/components/companies/shared/IntakeTabBar";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";
import { Label } from "@/components/ui/label";

import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { stateOptions } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { dates } from "@/lib/dates";
import { intake } from "@/lib/intakeProgress";
import { nullableNonOptionalNumberSchema } from "@/lib/schemas";
import { CompanyIntake, CompanyIntakeSite } from "@/types";

const siteSchema = z.object({
  id: z.string(),
  siteName: z.string().nullable(),
  address: z.string().nullable(),
  headCount: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  partnershipStartDate: z.string().nullable(),
  notes: z.string().nullable(),
  roleTypeBreakdown: z
    .object({
      production: nullableNonOptionalNumberSchema,
      skilledTrades: nullableNonOptionalNumberSchema,
      professional: nullableNonOptionalNumberSchema,
    })
    .nullable(),
  employmentTypeBreakdown: z
    .object({
      temp: nullableNonOptionalNumberSchema,
      contract: nullableNonOptionalNumberSchema,
      fullTime: nullableNonOptionalNumberSchema,
    })
    .nullable(),
});

const emptySite = (): CompanyIntakeSite => ({
  id: crypto.randomUUID(),
  siteName: null,
  address: null,
  headCount: null,
  city: null,
  state: null,
  zipCode: null,
  partnershipStartDate: null,
  notes: null,
  roleTypeBreakdown: {
    production: null,
    skilledTrades: null,
    professional: null,
  },
  employmentTypeBreakdown: { temp: null, contract: null, fullTime: null },
});

export const Sites = ({
  companyIntake,
  isEditing,
  setIsEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();
  const [sites, setSites] = useState<CompanyIntakeSite[]>(companyIntake.sites);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const primaryIndex = useMemo(() => {
    if (sites.length <= 1) return 0;
    const datedSites = sites
      .map((site, i) => ({ index: i, date: site.partnershipStartDate }))
      .filter((s): s is { index: number; date: string } => s.date !== null);
    if (datedSites.length === 0) return 0;
    datedSites.sort((a, b) => a.date.localeCompare(b.date));
    return datedSites[0]?.index ?? 0;
  }, [sites]);

  const saveAllSites = (updatedSites: CompanyIntakeSite[]) => {
    updateCompanyIntake({
      updateCompanyIntakeRequest: { sites: updatedSites },
    });
  };

  const handleAddSite = () => {
    const updated = [...sites, emptySite()];
    setSites(updated);
    setSelectedIndex(updated.length - 1);
    setIsEditing(true);
    saveAllSites(updated);
  };

  const handleDeleteSite = (index: number) => {
    const updated = sites.filter((_, i) => i !== index);
    setSites(updated);
    const newIndex = index < selectedIndex ? selectedIndex - 1 : selectedIndex;
    setSelectedIndex(Math.min(Math.max(0, newIndex), updated.length - 1));
    saveAllSites(updated);
  };

  const handleUpdateSite = (index: number, data: CompanyIntakeSite) => {
    mixpanelUtils.trackFormSubmission("Site add/edit", MIXPANEL_SCREENS.COMPANY);
    const updated = sites.map((s, i) => (i === index ? data : s));
    setSites(updated);
    saveAllSites(updated);
  };

  const render = () => {
    if (!sites.length) {
      return (
        <Column className="gap-6">
          <EmptyState
            description="Add a site to get started."
            handlePrimaryClick={() => {
              setIsEditing(true);
              handleAddSite();
            }}
            Icon={MapPinIcon}
            primaryButtonName="Add site"
            title="No sites yet"
          />
        </Column>
      );
    }

    return (
      <Column className="gap-4">
        {/* top offset = combined height of IntakeHeader */}
        <Column className="sticky top-[8.78rem] z-10 bg-white">
          <IntakeTabBar
            addLabel="Add Site"
            deleteDescription="This action cannot be undone. This will permanently delete the site."
            onAdd={handleAddSite}
            onDelete={() => {
              handleDeleteSite(selectedIndex);
            }}
            screenName={MIXPANEL_SCREENS.COMPANY}
          >
            {sites.map((site, index) => (
              <IntakeTab
                key={index}
                progress={intake.getSiteProgress(site)}
                isPrimary={index === primaryIndex}
                isSelected={selectedIndex === index}
                label={site.siteName ?? `Site ${String(index + 1)}`}
                onSelect={() => {
                  setSelectedIndex(index);
                }}
              />
            ))}
          </IntakeTabBar>
        </Column>
        {isEditing ? (
          <SiteForm
            key={`${String(selectedIndex)}-${String(sites.length)}`}
            index={selectedIndex}
            onSave={handleUpdateSite}
            site={sites[selectedIndex] ?? emptySite()}
          />
        ) : (
          <SiteReadOnly site={sites[selectedIndex] ?? emptySite()} />
        )}
        {isError && (
          <ErrorText errorMessage="An error occurred while saving. Please try again." />
        )}
      </Column>
    );
  };

  return render();
};

const SiteForm = ({
  index,
  onSave,
  site,
}: {
  index: number;
  onSave: (index: number, data: CompanyIntakeSite) => void;
  site: CompanyIntakeSite;
}) => {
  const form = useForm<z.infer<typeof siteSchema>>({
    resolver: zodResolver(siteSchema),
    defaultValues: site,
  });

  const saveOnBlur = () => {
    void form.handleSubmit((data) => {
      onSave(index, data);
    })();
  };

  const render = () => (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-12">
        <Column className="gap-4">
          <Label className="text-md font-semibold text-slate-700">
            Location
          </Label>
          <AutosaveFormTextInput
            fieldName="siteName"
            form={form}
            label="Site Name"
            onBlur={saveOnBlur}
            placeholder="e.g., Main Plant, Distribution Center, Mascot"
          />
          <AutosaveFormTextInput
            fieldName="address"
            form={form}
            label="Address"
            onBlur={saveOnBlur}
            placeholder="e.g., 21 Platform Way S"
          />
          <Row className="gap-4">
            <AutosaveFormTextInput
              className="flex-1"
              fieldName="city"
              form={form}
              label="City"
              onBlur={saveOnBlur}
              placeholder="e.g., Nashville"
            />
            <Row className="flex-1 gap-4">
              <AutosaveFormEnumSelect
                className="flex-1"
                fieldName="state"
                form={form}
                label="State"
                onSelect={saveOnBlur}
                options={stateOptions}
              />
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="zipCode"
                form={form}
                label="Zip Code"
                onBlur={saveOnBlur}
                placeholder="e.g., 37203"
              />
            </Row>
          </Row>
          <Row className="gap-4">
            <AutosaveFormTextInput
              className="flex-1"
              fieldName="headCount"
              form={form}
              label="Head Count"
              onBlur={saveOnBlur}
              placeholder="e.g., 200 or 150-250"
            />
            <AutosaveFormDateInput
              className="flex-1"
              fieldName="partnershipStartDate"
              form={form}
              infoTooltip="The site with the earliest partnership start date is marked as the primary site, shown with a star on the tab."
              label="Partnership Start Date"
              onBlur={saveOnBlur}
            />
          </Row>
        </Column>

        <Column className="gap-4">
          <Label className="text-md font-semibold text-slate-700">
            Role Type Breakdown (%)
          </Label>
          <Row className="gap-4">
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="roleTypeBreakdown.production"
              form={form}
              label="Production"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 50"
            />
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="roleTypeBreakdown.skilledTrades"
              form={form}
              label="Skilled Trades"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 30"
            />
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="roleTypeBreakdown.professional"
              form={form}
              label="Professional"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 20"
            />
          </Row>
        </Column>

        <Column className="gap-4">
          <Label className="text-md font-semibold text-slate-700">
            Employment Type Breakdown (%)
          </Label>
          <Row className="gap-4">
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="employmentTypeBreakdown.temp"
              form={form}
              label="Temp"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 40"
            />
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="employmentTypeBreakdown.contract"
              form={form}
              label="Contract"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 25"
            />
            <AutosaveFormNumberInput
              className="flex-1"
              fieldName="employmentTypeBreakdown.fullTime"
              form={form}
              label="Full Time"
              maxLength={3}
              onBlur={saveOnBlur}
              placeholder="e.g., 35"
            />
          </Row>
        </Column>

        <AutosaveFormRichTextInput
          editorHeight="150px"
          infoTooltip={`Are they experiencing unusually high churn or turnover?\nAre there plans to add a new line or expand capacity?\nDo they have seasonal staffing fluctuations?\nWhat are the key challenges or pain points at this location?`}
          fieldName="notes"
          form={form}
          label="Site Details"
          onBlur={saveOnBlur}
        />
      </Column>
    </IntakeForm>
  );

  return render();
};

const SiteReadOnly = ({ site }: { site: CompanyIntakeSite }) => {
  const stateLabel =
    stateOptions.find((o) => o.value === site.state)?.label ?? null;

  const numStr = (v: number | null | undefined) =>
    v != null ? String(v) : null;

  return (
    <Column className="gap-12">
      {/* Location */}
      <Column className="gap-4">
        <Label className="text-md font-semibold text-slate-700">Location</Label>
        <ReadOnlyField label="Site Name" value={site.siteName} />
        <ReadOnlyField label="Address" value={site.address} />
        <Row className="gap-4">
          <ReadOnlyField className="flex-1" label="City" value={site.city} />
          <Row className="flex-1 gap-4">
            <ReadOnlyField className="flex-1" label="State" value={stateLabel} />
            <ReadOnlyField
              className="flex-1"
              label="Zip Code"
              value={site.zipCode}
            />
          </Row>
        </Row>
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Head Count"
            value={site.headCount}
          />
          <ReadOnlyField
            className="flex-1"
            label="Partnership Start Date"
            value={
              site.partnershipStartDate
                ? dates.formatUtcDateForUser(site.partnershipStartDate)
                : null
            }
          />
        </Row>
      </Column>

      {/* Role Type Breakdown */}
      <Column className="gap-4">
        <Label className="text-md font-semibold text-slate-700">
          Role Type Breakdown (%)
        </Label>
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Production"
            value={numStr(site.roleTypeBreakdown?.production)}
          />
          <ReadOnlyField
            className="flex-1"
            label="Skilled Trades"
            value={numStr(site.roleTypeBreakdown?.skilledTrades)}
          />
          <ReadOnlyField
            className="flex-1"
            label="Professional"
            value={numStr(site.roleTypeBreakdown?.professional)}
          />
        </Row>
      </Column>

      {/* Employment Type Breakdown */}
      <Column className="gap-4">
        <Label className="text-md font-semibold text-slate-700">
          Employment Type Breakdown (%)
        </Label>
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Temp"
            value={numStr(site.employmentTypeBreakdown?.temp)}
          />
          <ReadOnlyField
            className="flex-1"
            label="Contract"
            value={numStr(site.employmentTypeBreakdown?.contract)}
          />
          <ReadOnlyField
            className="flex-1"
            label="Full Time"
            value={numStr(site.employmentTypeBreakdown?.fullTime)}
          />
        </Row>
      </Column>

      <ReadOnlyRichTextField label="Site Details" value={site.notes} />
    </Column>
  );
};
